import { create } from 'zustand';
import { applyColorScheme } from '@/theme/apply-color-scheme';
import type { ColorScheme } from '@/theme/types';

const STORAGE_PREFIX = 'dentxe.appearance';
const ANONYMOUS_USER_KEY = 'anonymous';

interface UserAppearanceState {
  userId: string | null;
  colorScheme: ColorScheme;
  setUserId: (userId: string | null) => void;
  setColorScheme: (scheme: ColorScheme) => void;
  toggleColorScheme: () => void;
  hydrateForUser: (userId: string | null) => void;
}

function storageKey(userId: string | null): string {
  return `${STORAGE_PREFIX}.${userId ?? ANONYMOUS_USER_KEY}`;
}

function readStoredScheme(userId: string | null): ColorScheme {
  const raw = localStorage.getItem(storageKey(userId));
  return raw === 'dark' || raw === 'light' ? raw : 'light';
}

function persistScheme(userId: string | null, scheme: ColorScheme): void {
  localStorage.setItem(storageKey(userId), scheme);
}

export const useUserAppearanceStore = create<UserAppearanceState>((set, get) => ({
  userId: null,
  colorScheme: readStoredScheme(null),

  setUserId(nextUserId) {
    const scheme = readStoredScheme(nextUserId);
    applyColorScheme(scheme);
    set({ userId: nextUserId, colorScheme: scheme });
  },

  setColorScheme(scheme) {
    const { userId } = get();
    persistScheme(userId, scheme);
    applyColorScheme(scheme);
    set({ colorScheme: scheme });
  },

  toggleColorScheme() {
    const next: ColorScheme = get().colorScheme === 'light' ? 'dark' : 'light';
    get().setColorScheme(next);
  },

  hydrateForUser(userId) {
    get().setUserId(userId);
  },
}));
